import { useState, useEffect, useCallback, useRef } from 'react';
import { bffClient } from '@sluitstraat/core';
import type { Address } from '../../../components/AddressSection';
import { mapTypeWoning, mapAardBouw, mapAardDak } from './infofolio';
import { cleanUnselectedCoveragePremiums, formatCurrency } from './calc';

// Owns the Woonhuis Infofolio machinery, moved verbatim from WoonhuisScreen: fetchPropertyInfo
// (including its requestAnimationFrame DOM writes and the Inboedel step-data sync), the monument
// flag, the infofolio-filled field locks and the pending-address trigger. The screen keeps
// setIsMonumentProperty (BA_MONUMEN field handler) and setPendingInfofolioAddress (loadSchema)
// from the return value.
//
// sessionStorage ownership: this hook owns 'woonhuis_monument_property' and
// 'woonhuis_infofolio_fields'. 'woonhuis_infofolio_address' is never written by SAA — do not add.
//
// Effect reorder note: this hook registers its effects earlier than they originally sat in
// WoonhuisScreen. All of them are state- or mount-driven (the pending-address trigger fires off
// state set via setTimeout/handlers), so there is no ordering dependency on the screen's effects.
//
// fetchPropertyInfo's dep array is EXACTLY [isBuildingDetailsLocked, selectedProducts,
// getStepData, saveStepData] — the saveWorkflowData staleness is accepted (it is a stable
// useCallback in the screen), do not "fix".

const SCREEN_ID = 'woonhuis';

interface InfofolioArgs {
  selectedProducts: any;
  getStepData: (stepId: string) => any;
  saveStepData: (stepId: string, data: any) => void;
  saveWorkflowData: (stepId: string, displayData: any) => void;
  setData: (updater: any) => void;
  setValidationErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  isBuildingDetailsLocked: boolean;
}

export function useInfofolio({
  selectedProducts,
  getStepData,
  saveStepData,
  saveWorkflowData,
  setData,
  setValidationErrors,
  isBuildingDetailsLocked,
}: InfofolioArgs) {
  // Infofolio property data state
  const [_propertyInfo, setPropertyInfo] = useState<any>(null);
  const [isLoadingPropertyInfo, setIsLoadingPropertyInfo] = useState(false);

  // Monument property detection - monuments cannot be insured via SAA
  // Persist in sessionStorage to survive page navigation (cleared on full app reload in main.tsx)
  const [isMonumentProperty, setIsMonumentProperty] = useState(() => {
    try {
      const saved = sessionStorage.getItem('woonhuis_monument_property');
      return saved === 'true';
    } catch (e) {
      return false;
    }
  });

  // Persist monument state to sessionStorage when it changes
  useEffect(() => {
    try {
      sessionStorage.setItem('woonhuis_monument_property', String(isMonumentProperty));
    } catch (e) {
      // Ignore sessionStorage errors
    }
  }, [isMonumentProperty]);

  // Track pending Infofolio lookup when address needs Infofolio data (uses state to trigger useEffect)
  const [pendingInfofolioAddress, setPendingInfofolioAddress] = useState<{ postalCode: string; houseNumber: string; addition?: string } | null>(null);

  // Track the last address for which Infofolio was called (to detect address changes)
  const lastInfofolioAddress = useRef<string>('');

  // Track which fields were filled by Infofolio (these become readonly)
  // Stored in sessionStorage to persist across navigation (separate from form data)
  const [infofolioFilledFields, setInfofolioFilledFields] = useState<Set<string>>(() => {
    try {
      const saved = sessionStorage.getItem('woonhuis_infofolio_fields');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          const set = new Set<string>(parsed);
          // Migration: older sessions may have been saved before 'monumen' was tracked.
          // If Infofolio ran (any address-derived field present), 'monumen' is implied.
          if (set.size > 0) set.add('monumen');
          set.delete('permbew'); // older sessions locked it; Infofolio never answers it
          return set;
        }
      }
    } catch (e) {
      // Ignore parse errors
    }
    return new Set();
  });

  // Restore infofolio fields on mount (in case useState initializer didn't run)
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem('woonhuis_infofolio_fields');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const set = new Set<string>(parsed);
          // Migration: older sessions may have been saved before 'monumen' was tracked.
          set.add('monumen');
          set.delete('permbew'); // older sessions locked it; Infofolio never answers it
          setInfofolioFilledFields(set);
        }
      }
    } catch (e) {
      // Ignore parse errors
    }
  }, []); // Run once on mount

  // Fetch property info from Infofolio after successful address validation
  const fetchPropertyInfo = useCallback(async (postalCode: string, houseNumber: string, addition?: string) => {
    try {
      setIsLoadingPropertyInfo(true);

      const response = await bffClient.getPropertyInfo(postalCode, houseNumber, addition || undefined);

      setPropertyInfo(response);

      // Extract property data from BFF response (nested under BA)
      const propertyData = response?.BA;

      if (!isBuildingDetailsLocked && propertyData) {
        // Pre-calculate all mapped values
        const mappedTypeWon = mapTypeWoning(propertyData.BA_TYPEWON);
        const mappedAardBw = mapAardBouw(propertyData.BA_AARDBW);
        // Use smart roof mapping with dakconstructie/dakbedekking from Infofolio
        const mappedAardDak = mapAardDak(
          propertyData.BA_AARDDAK,
          propertyData.BA_DAKCONS,      // dakconstructie (e.g., "Schuin", "Plat")
          propertyData.BA_DAKBED,       // dakbedekking (e.g., "Pannen", "Bitumen", "Riet")
          propertyData.BA_DAKBED_CODE   // dakbedekking_code (e.g., "60" = Riet, "70" = Kunstriet)
        );
        // BA_EIGENWO (Eigen woning) is prefilled from the Infofolio koop/huur flag:
        // "Koop" -> 'J' (eigen woning), anything else (e.g. "Huur") -> 'N'.
        const mappedEigenwo = propertyData.BA_EIGENWO
          ? (String(propertyData.BA_EIGENWO).toLowerCase().startsWith('koop') ? 'J' : 'N')
          : '';
        // BA_WONING (Gebruik pand) is deliberately NOT prefilled — SAA requires the user to choose it.

        // Check monument status from Infofolio (BA_MONUMEN field)
        // Values: 'J'/'Y' = is monument, 'N' = not monument
        const monumentValue = propertyData.BA_MONUMEN;
        const isMonument = monumentValue === 'J' || monumentValue === 'Y';

        // Update monument state - monuments cannot be insured via SAA
        setIsMonumentProperty(isMonument);

        // console.log('[Woonhuis] Mapped values:', {
        //   bouwjaar: propertyData.BA_BOUWJR,
        //   oppwon: propertyData.BA_OPPWON,
        //   herbwrd: propertyData.BA_HERBWRD,
        //   typewon: { raw: propertyData.BA_TYPEWON, mapped: mappedTypeWon },
        //   aardbw: { raw: propertyData.BA_AARDBW, mapped: mappedAardBw },
        //   aarddak: { raw: propertyData.BA_AARDDAK, mapped: mappedAardDak },
        // });

        setData((prevData: any) => {
          const updatedData = { ...prevData };
          let hasChanges = false;
          const fieldsUpdated: string[] = [];

          // Ensure nested structure exists
          if (!updatedData.Contractdocument) updatedData.Contractdocument = {};
          if (!updatedData.Contractdocument.PP) updatedData.Contractdocument.PP = {};
          if (!updatedData.Contractdocument.PP.BA) updatedData.Contractdocument.PP.BA = {};
          if (!updatedData.Contractdocument.PP.OP) updatedData.Contractdocument.PP.OP = {};

          // Update bouwjaar (building year)
          if (propertyData.BA_BOUWJR) {
            updatedData.Contractdocument.PP.BA.BA_BOUWJR = propertyData.BA_BOUWJR;
            hasChanges = true;
            fieldsUpdated.push('bouwjaar');
          }

          // Update oppervlakte woning (property size) - background value, no form field
          if (propertyData.BA_OPPWON) {
            updatedData.Contractdocument.PP.BA.BA_OPPWON = propertyData.BA_OPPWON;
            hasChanges = true;
            fieldsUpdated.push('oppwon');
          }

          // Update herbouwwaarde (rebuild value) -> both BA_HERBWRD and OP_VERZSOM
          if (propertyData.BA_HERBWRD) {
            updatedData.Contractdocument.PP.BA.BA_HERBWRD = propertyData.BA_HERBWRD;
            updatedData.Contractdocument.PP.OP.OP_VERZSOM = propertyData.BA_HERBWRD;
            hasChanges = true;
            fieldsUpdated.push('verzsom');
          }

          // Update type woning (house type)
          if (mappedTypeWon) {
            updatedData.Contractdocument.PP.BA.BA_TYPEWON = mappedTypeWon;
            hasChanges = true;
            fieldsUpdated.push('typewon');
          }

          // Update aard bouw (wall construction)
          if (mappedAardBw) {
            updatedData.Contractdocument.PP.BA.BA_AARDBW = mappedAardBw;
            hasChanges = true;
            fieldsUpdated.push('aardbw');
          }

          // Update aard dak (roof type)
          if (mappedAardDak) {
            updatedData.Contractdocument.PP.BA.BA_AARDDAK = mappedAardDak;
            hasChanges = true;
            fieldsUpdated.push('aarddak');
          }

          // Update eigen woning (Koop/Huur from Infofolio)
          if (mappedEigenwo) {
            updatedData.Contractdocument.PP.BA.BA_EIGENWO = mappedEigenwo;
            hasChanges = true;
            fieldsUpdated.push('eigenwo');
          }

          // Update oppervlakte begane grond (ground floor area)
          if (propertyData.BA_BGM2) {
            updatedData.Contractdocument.PP.BA.BA_BGM2 = propertyData.BA_BGM2;
            hasChanges = true;
            fieldsUpdated.push('bgm2');
          }

          // Update totaaloppervlakte bijgebouwen (outbuildings area) - only when infofolio provides an actual value
          if (propertyData.BA_TOPPBGB && Number(propertyData.BA_TOPPBGB) > 0) {
            updatedData.Contractdocument.PP.BA.BA_TOPPBGB = propertyData.BA_TOPPBGB;
            hasChanges = true;
            fieldsUpdated.push('toppbgb');
          }

          // Prefill permanent bewoond. NOT an Infofolio answer, so it is never locked and an
          // answer the customer already gave is kept.
          if (!updatedData.Contractdocument.PP.BA.BA_PERMBEW) updatedData.Contractdocument.PP.BA.BA_PERMBEW = 'J';
          hasChanges = true;

          // Set herbouwwaarde vaststelling to Maatschappij when infofolio provided rebuild value
          if (propertyData.BA_HERBWRD) {
            updatedData.Contractdocument.PP.BA.BA_HERBVST = 'M';
            // Mirror the BA_HERBVST→OP_GARONDV rule (same as the user-driven handler):
            // 'M' (Waardebepaling maatschappij) implies the under-insurance guarantee is included.
            if (!updatedData.Contractdocument.PP.OP) updatedData.Contractdocument.PP.OP = {};
            updatedData.Contractdocument.PP.OP.OP_GARONDV = 'J';
            hasChanges = true;
            fieldsUpdated.push('herbvst');
          }

          // Set monument status in data (already checked above)
          updatedData.Contractdocument.PP.BA.BA_MONUMEN = isMonument ? 'J' : 'N';
          if (isMonument) {
            hasChanges = true;
          }

          if (hasChanges) {
            // Clean and save the data
            const cleanedData = cleanUnselectedCoveragePremiums(updatedData);
            saveWorkflowData(SCREEN_ID, cleanedData);

            // Sync to Inboedel if it exists
            if (selectedProducts?.inboedel) {
              const inboedelData = getStepData('inboedel');
              if (inboedelData) {
                if (!inboedelData.Contractdocument.PP.BA) inboedelData.Contractdocument.PP.BA = {};
                if (!inboedelData.Contractdocument.PP.OP) inboedelData.Contractdocument.PP.OP = {};
                if (propertyData.BA_BOUWJR) inboedelData.Contractdocument.PP.BA.BA_BOUWJR = propertyData.BA_BOUWJR;
                if (propertyData.BA_OPPWON) inboedelData.Contractdocument.PP.BA.BA_OPPWON = propertyData.BA_OPPWON;
                if (propertyData.BA_HERBWRD) {
                  inboedelData.Contractdocument.PP.BA.BA_HERBWRD = propertyData.BA_HERBWRD;
                  inboedelData.Contractdocument.PP.BA.BA_HERBVST = 'M'; // Maatschappij - Infofolio determination
                  // NOTE: Do NOT sync herbouwwaarde to inboedel OP_VERZSOM
                  // Inboedel OP is Eigenaarsbelang (max 50K), not Opstal (building value)
                }
                if (propertyData.BA_BGM2) inboedelData.Contractdocument.PP.BA.BA_BGM2 = propertyData.BA_BGM2;
                if (mappedTypeWon) inboedelData.Contractdocument.PP.BA.BA_TYPEWON = mappedTypeWon;
                if (mappedAardBw) inboedelData.Contractdocument.PP.BA.BA_AARDBW = mappedAardBw;
                if (mappedAardDak) inboedelData.Contractdocument.PP.BA.BA_AARDDAK = mappedAardDak;
                if (mappedEigenwo) inboedelData.Contractdocument.PP.BA.BA_EIGENWO = mappedEigenwo;
                // Sync monument status to Inboedel
                inboedelData.Contractdocument.PP.BA.BA_MONUMEN = isMonument ? 'J' : 'N';
                saveStepData('inboedel', inboedelData);
              }
            }
          }

          return hasChanges ? updatedData : prevData;
        });

        // Track which fields were filled by Infofolio (make them readonly)
        const filledFields = new Set<string>();
        if (propertyData.BA_BOUWJR) filledFields.add('bouwjaar');
        if (mappedTypeWon) filledFields.add('typewon');
        if (mappedAardBw) filledFields.add('aardbw');
        if (mappedAardDak) filledFields.add('aarddak');
        if (mappedEigenwo) filledFields.add('eigenwo');
        if (propertyData.BA_HERBWRD) filledFields.add('verzsom');
        if (propertyData.BA_BGM2) filledFields.add('bgm2');
        if (propertyData.BA_TOPPBGB && Number(propertyData.BA_TOPPBGB) > 0) filledFields.add('toppbgb');
        if (propertyData.BA_HERBWRD) filledFields.add('herbvst');
        // BA_MONUMEN is always set ('J' or 'N') after a successful Infofolio fetch
        filledFields.add('monumen');

        if (filledFields.size > 0) {
          setInfofolioFilledFields(filledFields);
          // Persist to sessionStorage so it survives navigation
          sessionStorage.setItem('woonhuis_infofolio_fields', JSON.stringify(Array.from(filledFields)));

          // Clear validation errors for fields that were filled by Infofolio
          setValidationErrors(prev => {
            const updated = { ...prev };
            if (propertyData.BA_BOUWJR) delete updated['Contractdocument.PP.BA.BA_BOUWJR'];
            if (mappedTypeWon) delete updated['Contractdocument.PP.BA.BA_TYPEWON'];
            if (mappedAardBw) delete updated['Contractdocument.PP.BA.BA_AARDBW'];
            if (mappedEigenwo) delete updated['Contractdocument.PP.BA.BA_EIGENWO'];
            if (mappedAardDak) delete updated['Contractdocument.PP.BA.BA_AARDDAK'];
            if (propertyData.BA_HERBWRD) delete updated['Contractdocument.PP.OP.OP_VERZSOM'];
            if (propertyData.BA_BGM2) delete updated['Contractdocument.PP.BA.BA_BGM2'];
            delete updated['Contractdocument.PP.BA.BA_TOPPBGB'];
            delete updated['Contractdocument.PP.BA.BA_PERMBEW'];
            if (propertyData.BA_HERBWRD) delete updated['Contractdocument.PP.BA.BA_HERBVST'];
            delete updated['Contractdocument.PP.BA.BA_MONUMEN'];
            return updated;
          });
        }

        // Update DOM elements after state update (next frame to ensure React has rendered)
        requestAnimationFrame(() => {
          if (propertyData.BA_BOUWJR) {
            const input = document.getElementById('bouwjaar') as HTMLInputElement;
            if (input) input.value = String(propertyData.BA_BOUWJR);
          }
          if (propertyData.BA_HERBWRD) {
            const input = document.getElementById('verzekerd-bedrag') as HTMLInputElement;
            if (input) input.value = formatCurrency(propertyData.BA_HERBWRD);
          }
          if (mappedTypeWon) {
            const select = document.getElementById('typewon') as HTMLSelectElement;
            if (select) select.value = mappedTypeWon;
          }
          if (mappedAardBw) {
            const select = document.getElementById('aardbw') as HTMLSelectElement;
            if (select) select.value = mappedAardBw;
          }
          if (mappedAardDak) {
            const select = document.getElementById('aarddak') as HTMLSelectElement;
            if (select) select.value = mappedAardDak;
          }
          if (propertyData.BA_BGM2) {
            const input = document.getElementById('bgm2') as HTMLInputElement;
            if (input) input.value = String(propertyData.BA_BGM2);
          }
          if (propertyData.BA_TOPPBGB && Number(propertyData.BA_TOPPBGB) > 0) {
            const input = document.getElementById('toppbgb') as HTMLInputElement;
            if (input) input.value = String(propertyData.BA_TOPPBGB);
          }
          if (propertyData.BA_HERBWRD) {
            const select = document.getElementById('herbvst') as HTMLSelectElement;
            if (select) select.value = 'M';
          }
        });
      }
    } catch (error) {
      // Silently fail - Infofolio data is optional enhancement
      setPropertyInfo(null);
      // Clear infofolio-filled tracking so fields remain editable after a failed lookup
      setInfofolioFilledFields(new Set());
      sessionStorage.removeItem('woonhuis_infofolio_fields');
    } finally {
      setIsLoadingPropertyInfo(false);
    }
  }, [isBuildingDetailsLocked, selectedProducts, getStepData, saveStepData, saveWorkflowData, setData, setValidationErrors]);

  // Trigger Infofolio lookup when address was copied from AVP and Inboedel is not selected
  useEffect(() => {
    if (pendingInfofolioAddress) {
      const { postalCode, houseNumber, addition } = pendingInfofolioAddress;
      setPendingInfofolioAddress(null); // Clear pending so it only triggers once
      fetchPropertyInfo(postalCode, houseNumber, addition);
    }
  }, [pendingInfofolioAddress, fetchPropertyInfo]);

  // Wipe-and-refetch on a committed, resolved address change: reset the infofolio field locks
  // when the address identity changed, then queue a fresh Infofolio lookup. Consolidates the two
  // identical tails of handlePolicyholderAddressChange and handleRiskAddressChange (their
  // differing guards stay in the screen).
  const refetchForAddress = useCallback((a: Address) => {
    const key = `${a.postcode}|${a.houseNumber}|${a.addition || ''}`;
    if (lastInfofolioAddress.current && lastInfofolioAddress.current !== key) {
      setInfofolioFilledFields(new Set());
      if (typeof window !== 'undefined') sessionStorage.removeItem('woonhuis_infofolio_fields');
    }
    lastInfofolioAddress.current = key;
    setPendingInfofolioAddress({ postalCode: a.postcode, houseNumber: a.houseNumber, addition: a.addition || undefined });
  }, []);

  return {
    isMonumentProperty,
    setIsMonumentProperty,
    isLoadingPropertyInfo,
    infofolioFilledFields,
    setInfofolioFilledFields,
    setPendingInfofolioAddress,
    refetchForAddress,
  };
}
